import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Search, 
  Trash2, 
  Eye, 
  Calendar,
  Image as ImageIcon,
  X,
  Upload,
  Grid3X3,
  List,
  Folder,
  Loader2
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useGalerie } from '../../hooks/useGalerie';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.75rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }
  }
`;

const ViewToggle = styled.div`
  display: flex;
  background: var(--white);
  border-radius: var(--radius-md);
  padding: 0.25rem;
  border: 2px solid var(--border-light);

  button {
    padding: 0.5rem;
    border-radius: var(--radius-sm);
    color: var(--text-light);

    &.active {
      background: var(--primary-blue);
      color: var(--white);
    }
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--primary-blue), #004d8f);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
  }

  .content {
    .value {
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--text-dark);
      font-family: var(--font-secondary);
    }

    .label {
      font-size: 0.85rem;
      color: var(--text-light);
    }
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  transition: var(--transition);

  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-4px);

    .overlay {
      opacity: 1;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  background: var(--background-light);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-light);
  }

  .overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    opacity: 0;
    transition: var(--transition);

    button {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: var(--transition);

      &.view {
        background: var(--white);
        color: var(--primary-blue);

        &:hover {
          transform: scale(1.1);
        }
      }

      &.delete {
        background: var(--secondary-red);
        color: var(--white);

        &:hover {
          transform: scale(1.1);
        }
      }
    }
  }
`;

const ImageInfo = styled.div`
  padding: 1rem;

  .title {
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .date {
    font-size: 0.85rem;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
`;

const AlbumSelector = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const AlbumButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: 0.9rem;
  transition: var(--transition);
  background: ${props => props.$active ? 'var(--primary-blue)' : 'var(--white)'};
  color: ${props => props.$active ? 'var(--white)' : 'var(--text-dark)'};
  border: 2px solid ${props => props.$active ? 'var(--primary-blue)' : 'var(--border-light)'};

  &:hover {
    border-color: var(--primary-blue);
  }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ImageModal = styled.div`
  max-width: 90vw;
  max-height: 90vh;
  position: relative;

  img {
    max-width: 100%;
    max-height: 85vh;
    border-radius: var(--radius-lg);
  }

  button {
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    color: var(--white);
    cursor: pointer;

    &:hover {
      color: var(--accent-gold);
    }
  }
`;

const UploadModal = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 600px;
  box-shadow: var(--shadow-xl);
`;

const UploadZone = styled.div`
  border: 3px dashed var(--border-light);
  border-radius: var(--radius-lg);
  padding: 3rem;
  text-align: center;
  margin: 1.5rem;
  transition: var(--transition);

  &:hover {
    border-color: var(--primary-blue);
    background: var(--background-light);
  }

  svg {
    color: var(--primary-blue);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.1rem;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9rem;
    color: var(--text-medium);
    margin-bottom: 0.5rem;
  }

  span {
    font-size: 0.8rem;
    color: var(--text-light);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 2px solid var(--border-light);

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.5rem;
    color: var(--text-dark);
  }

  button {
    background: none;
    color: var(--text-light);
    cursor: pointer;

    &:hover {
      color: var(--secondary-red);
    }
  }
`;

const AdminGalerie = () => {
  const { photos, albums, loading, uploadProgress, uploadPhoto, deletePhoto } = useGalerie();
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAlbum, setSelectedAlbum] = useState('all');
  const [viewingImage, setViewingImage] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', album: 'general' });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Construire la liste des albums avec comptage
  const albumList = [
    { id: 'all', label: 'Toutes les photos', count: photos.length },
    ...albums.map(album => ({
      id: album,
      label: album.charAt(0).toUpperCase() + album.slice(1),
      count: photos.filter(p => p.album === album).length
    }))
  ];

  const filteredPhotos = selectedAlbum === 'all'
    ? photos
    : photos.filter(img => img.album === selectedAlbum);

  const handleDelete = async (id, imagePath) => {
    if (window.confirm('Supprimer cette image ')) {
      const result = await deletePhoto(id, imagePath);
      if (!result.success) {
        alert('Erreur: ' + result.error);
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image (JPEG, PNG)');
      return;
    }

    setIsUploading(true);
    const result = await uploadPhoto(file, uploadForm);
    setIsUploading(false);

    if (result.success) {
      setIsUploadModalOpen(false);
      setUploadForm({ title: '', album: 'general' });
    } else {
      alert('Erreur upload: ' + result.error);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image (JPEG, PNG)');
      return;
    }

    setIsUploading(true);
    const result = await uploadPhoto(file, uploadForm);
    setIsUploading(false);

    if (result.success) {
      setIsUploadModalOpen(false);
      setUploadForm({ title: '', album: 'general' });
    } else {
      alert('Erreur upload: ' + result.error);
    }
  };

  return (
    <AdminLayout title="Gestion de la Galerie">
      <HeaderActions>
        <SearchBox>
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher une photo..."
          />
        </SearchBox>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <ViewToggle>
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 size={20} />
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <List size={20} />
            </button>
          </ViewToggle>
          <AddButton onClick={() => setIsUploadModalOpen(true)}>
            <Plus size={20} />
            Ajouter des photos
          </AddButton>
        </div>
      </HeaderActions>

      <StatsGrid>
        <StatCard>
          <div className="icon">
            <ImageIcon size={24} />
          </div>
          <div className="content">
            <div className="value">{loading ? '...' : photos.length}</div>
            <div className="label">Photos totales</div>
          </div>
        </StatCard>
        <StatCard>
          <div className="icon">
            <Folder size={24} />
          </div>
          <div className="content">
            <div className="value">{loading ? '...' : albums.length}</div>
            <div className="label">Albums</div>
          </div>
        </StatCard>
        <StatCard>
          <div className="icon">
            <Eye size={24} />
          </div>
          <div className="content">
            <div className="value">
              {loading ? '...' : photos.reduce((acc, p) => acc + (p.views || 0), 0).toLocaleString()}
            </div>
            <div className="label">Vues totales</div>
          </div>
        </StatCard>
      </StatsGrid>

      <AlbumSelector>
        {albumList.map(album => (
          <AlbumButton 
            key={album.id}
            $active={selectedAlbum === album.id}
            onClick={() => setSelectedAlbum(album.id)}
          >
            {album.label} ({album.count})
          </AlbumButton>
        ))}
      </AlbumSelector>

      <GalleryGrid>
        {loading ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '3rem' }}>
            <Loader2 size={48} className="animate-spin" />
            <p>Chargement...</p>
          </div>
        ) : filteredPhotos.length === 0 ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '3rem', color: 'var(--text-light)' }}>
            <ImageIcon size={64} />
            <p>Aucune photo dans cet album</p>
          </div>
        ) : (
          filteredPhotos.map(image => (
            <GalleryItem key={image.id}>
              <ImageContainer>
                {image.imageUrl ? (
                  <img src={image.imageUrl} alt={image.title} loading="lazy" />
                ) : (
                  <div className="placeholder">
                    <ImageIcon size={48} />
                  </div>
                )}
                <div className="overlay">
                  <button className="view" onClick={() => setViewingImage(image)}>
                    <Eye size={20} />
                  </button>
                  <button className="delete" onClick={() => handleDelete(image.id, image.imagePath)}>
                    <Trash2 size={20} />
                  </button>
                </div>
              </ImageContainer>
              <ImageInfo>
                <div className="title">{image.title}</div>
                <div className="date">
                  <Calendar size={14} />
                  {image.date.toLocaleDateString('fr-FR')}
                </div>
              </ImageInfo>
            </GalleryItem>
          ))
        )}
      </GalleryGrid>

      {viewingImage && (
        <ModalOverlay onClick={() => setViewingImage(null)}>
          <ImageModal>
            {viewingImage.imageUrl ? (
              <img src={viewingImage.imageUrl} alt={viewingImage.title} />
            ) : (
              <div style={{ background: '#ddd', width: '100%', height: '100%', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-lg)' }}>
                <ImageIcon size={64} color="#999" />
              </div>
            )}
            <button onClick={() => setViewingImage(null)}>
              <X size={32} />
            </button>
          </ImageModal>
        </ModalOverlay>
      )}

      {isUploadModalOpen && (
        <ModalOverlay onClick={() => !isUploading && setIsUploadModalOpen(false)}>
          <UploadModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Ajouter des photos</h2>
              {!isUploading && (
                <button onClick={() => setIsUploadModalOpen(false)}>
                  <X size={24} />
                </button>
              )}
            </ModalHeader>
            
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Titre</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Titre de la photo"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '1rem'
                  }}
                  disabled={isUploading}
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Album</label>
                <select
                  value={uploadForm.album}
                  onChange={(e) => setUploadForm({ ...uploadForm, album: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '1rem'
                  }}
                  disabled={isUploading}
                >
                  <option value="general">Général</option>
                  <option value="evenements">Événements</option>
                  <option value="mediations">Médiations</option>
                  <option value="formations">Formations</option>
                  <option value="visites">Visites</option>
                </select>
              </div>
            </div>
            
            <UploadZone 
              onClick={() => !isUploading && fileInputRef.current.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{ opacity: isUploading ? 0.5 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/jpeg,image/png,image/jpg"
                style={{ display: 'none' }}
                disabled={isUploading}
              />
              
              {isUploading ? (
                <>
                  <Loader2 size={48} className="animate-spin" />
                  <h3>Upload en cours...</h3>
                  <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', marginTop: '1rem' }}>
                    <div 
                      style={{ 
                        width: `${uploadProgress}%`, 
                        height: '100%', 
                        background: 'var(--primary-blue)', 
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }} 
                    />
                  </div>
                  <p>{uploadProgress}%</p>
                </>
              ) : (
                <>
                  <Upload size={48} />
                  <h3>Cliquez ou glissez-déposez une photo</h3>
                  <p>JPEG, PNG - Max 10 Mo</p>
                </>
              )}
            </UploadZone>
          </UploadModal>
        </ModalOverlay>
      )}
    </AdminLayout>
  );
};

export default AdminGalerie;
